import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { useWeb3 } from '../../context/Web3Context';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { formatUsdc, formatZenova, parseUsdc } from '../../utils/format';
import { ADDR, ZENOVA_DECIMALS } from '../../config/contracts';

export const TokenReserves = () => {
  const { account } = useWeb3();
  const { storageW, usdcR, zenovaR, zenovaW } = useContracts();
  const notify = useNotify();

  const [usdcBal, setUsdcBal]     = useState(null);
  const [zenovaBal, setZenovaBal] = useState(null);
  const [zenovaAllow, setZenovaAllow] = useState(null);

  const [depAmt, setDepAmt] = useState('');
  const [wdUsdc, setWdUsdc] = useState('');
  const [wdUsdcTo, setWdUsdcTo] = useState('');
  const [wdZen, setWdZen] = useState('');
  const [wdZenTo, setWdZenTo] = useState('');

  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!usdcR || !zenovaR) return;
      try {
        const [u, z] = await Promise.all([
          usdcR.balanceOf(ADDR.storage),
          zenovaR.balanceOf(ADDR.storage)
        ]);
        if (cancelled) return;
        setUsdcBal(u);
        setZenovaBal(z);
        if (account) {
          const a = await zenovaR.allowance(account, ADDR.storage);
          if (cancelled) return;
          setZenovaAllow(a);
        }
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [usdcR, zenovaR, account, reload]);

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const tx = await fn();
      notify.info(`${label}: submitted`);
      await tx.wait();
      notify.success(`${label}: success`);
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  const deposit = async () => {
    if (!depAmt || !zenovaW || !storageW) return;
    const amt = ethers.utils.parseUnits(depAmt, ZENOVA_DECIMALS);

    setBusy('dep');
    try {
      if (!zenovaAllow || zenovaAllow.lt(amt)) {
        notify.info('Approving ZENOVA...');
        const ap = await zenovaW.approve(ADDR.storage, ethers.constants.MaxUint256);
        await ap.wait();
      }
      const tx = await storageW.depositZenova(amt);
      notify.info('Depositing...');
      await tx.wait();
      notify.success('ZENOVA deposited');
      setDepAmt('');
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="card mb-24">
        <div className="section-title">Contract Reserves</div>
        <div className="admin-form-grid">
          <div className="admin-metric">
            <div className="metric-label">USDC (deposits)</div>
            <div className="metric-value">${formatUsdc(usdcBal)}</div>
          </div>
          <div className="admin-metric">
            <div className="metric-label">ZENOVA (payouts)</div>
            <div className="metric-value">{formatZenova(zenovaBal)}</div>
          </div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="section-title">Deposit ZENOVA Reserve</div>
        <div className="input-group">
          <label className="input-label">Amount</label>
          <input className="input" type="number" placeholder="10000" value={depAmt} onChange={(e) => setDepAmt(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={busy === 'dep' || !depAmt} onClick={deposit}>
          {busy === 'dep' && <ButtonSpinner />}Approve & Deposit
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Emergency Withdraw USDC</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Amount (USDC)</label>
            <input className="input" type="number" value={wdUsdc} onChange={(e) => setWdUsdc(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">To Address</label>
            <input className="input" placeholder="0x..." value={wdUsdcTo} onChange={(e) => setWdUsdcTo(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-danger" disabled={busy === 'wdu' || !wdUsdc || !wdUsdcTo}
          onClick={() => run('wdu', () => storageW.ownerWithdrawUsdc(wdUsdcTo, parseUsdc(wdUsdc)))}>
          {busy === 'wdu' && <ButtonSpinner />}Withdraw USDC
        </button>
      </div>

      <div className="card">
        <div className="section-title">Withdraw ZENOVA Reserve</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Amount (ZENOVA)</label>
            <input className="input" type="number" value={wdZen} onChange={(e) => setWdZen(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">To Address</label>
            <input className="input" placeholder="0x..." value={wdZenTo} onChange={(e) => setWdZenTo(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-danger" disabled={busy === 'wdz' || !wdZen || !wdZenTo}
          onClick={() => run('wdz', () => storageW.ownerWithdrawZenova(wdZenTo, ethers.utils.parseUnits(wdZen, ZENOVA_DECIMALS)))}>
          {busy === 'wdz' && <ButtonSpinner />}Withdraw ZENOVA
        </button>
      </div>
    </div>
  );
};
